import React, { useState, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';
import './App.css';
import FileUploader from './FileUploader';
import FilePreview from './FilePreview';
import Message from './Message';
import { AwsRum } from 'aws-rum-web';

let awsRum = null
try {
  const config = {
    sessionSampleRate: 1,
    identityPoolId: window.env.REACT_APP_RUM_IDENTITY_POOL_ID || process.env.REACT_APP_RUM_IDENTITY_POOL_ID,
    endpoint: window.env.REACT_APP_RUM_ENDPOINT || process.env.REACT_APP_RUM_ENDPOINT,
    telemetries: ["http","errors","performance"],
    allowCookies: true,
    enableXRay: true,
    signing: true
  };

  const APPLICATION_ID = window.env.REACT_APP_RUM_APP_MONITOR_ID || process.env.REACT_APP_RUM_APP_MONITOR_ID;
  const APPLICATION_VERSION = '1.0.0';
  const APPLICATION_REGION = window.env.REACT_APP_RUM_REGION || process.env.REACT_APP_RUM_REGION;

  awsRum = new AwsRum(
    APPLICATION_ID,
    APPLICATION_VERSION,
    APPLICATION_REGION,
    config
  );
} catch (error) {}

const PRESIGNED_URL_ENDPOINT = process.env.REACT_APP_PRESIGNED_URL_ENDPOINT || '/api/v1/presigned-url';
const VALIDATE_ENDPOINT = process.env.REACT_APP_VALIDATE_URL_ENDPOINT || '/api/v1/validate';

const SESSION_ID_KEY = 'session_id';
const MAGIC_WORD_KEY = 'word';
const USER_NAME_KEY = 'user_name';

const UPLOAD_BATCH_SIZE = parseInt(process.env.REACT_APP_UPLOAD_BATCH_SIZE, 10) || 2;
const MAX_RETRIES = parseInt(process.env.REACT_APP_MAX_RETRIES, 10) || 3;
const RETRY_DELAY = parseInt(process.env.REACT_APP_RETRY_DELAY, 10) || 1000;

function App() {
  const [files, setFiles] = useState([]);
  const [name, setName] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadInitiated, setUploadInitiated] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [uploadComplete, setUploadComplete] = useState(false);
  const [magicWordState, setMagicWordState] = useState('validating');

  useEffect(() => {
    if (!sessionStorage.getItem(SESSION_ID_KEY)) {
      sessionStorage.setItem(SESSION_ID_KEY, uuidv4());
    }

    const urlParams = new URLSearchParams(window.location.search);
    const magicWord = urlParams.get('word');
    if (magicWord) {
      sessionStorage.setItem(MAGIC_WORD_KEY, magicWord);
      validateMagicWord(magicWord);
    } else {
      const storedMagicWord = sessionStorage.getItem(MAGIC_WORD_KEY);
      if (storedMagicWord) {
        validateMagicWord(storedMagicWord);
      } else {
        setMagicWordState('missing');
        if (awsRum) {
          awsRum.recordEvent('pageLoadWithoutWord', {})
        }
      }
    }
  }, []);

  useEffect(() => {
    const storedName = sessionStorage.getItem(USER_NAME_KEY);
    if (storedName) {
      setName(storedName);
    }
  }, []);

  useEffect(() => {
    if (name) {
      sessionStorage.setItem(USER_NAME_KEY, name);
    }
  }, [name]);

  const validateMagicWord = async (magicWord) => {
    try {
      const response = await fetch(VALIDATE_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ magicWord }),
      });
      if (response.ok) {
        const { isValid } = await response.json();
        setMagicWordState(isValid ? 'valid' : 'invalid');
      } else {
        setMagicWordState('invalid');
      }
    } catch (error) {
      setMagicWordState('invalid');
    }
  };

  const handleFilesSelected = (selectedFiles) => {
    const newFiles = selectedFiles.map(file => ({
      id: uuidv4(),
      file,
      status: 'pending',
      error: null,
    }));

    setFiles(prevFiles => {
      const existingFiles = new Set(prevFiles.map(f => f.file.name));
      const uniqueNewFiles = newFiles.filter(f => !existingFiles.has(f.file.name));
      return [...prevFiles, ...uniqueNewFiles];
    });
  };

  const handleRemoveFile = (fileIdToRemove) => {
    setFiles(prevFiles => prevFiles.filter(file => file.id !== fileIdToRemove));
  };

  const uploadFile = async (file, fileId, retries = 0) => {
    try {
      setFiles(prev => prev.map(f => f.id === fileId ? { ...f, status: 'uploading' } : f));

      const presignedUrlResponse = await fetch(PRESIGNED_URL_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          fileName: file.name,
          sessionId: sessionStorage.getItem(SESSION_ID_KEY),
          magicWord: sessionStorage.getItem(MAGIC_WORD_KEY),
          name,
        }),
      });

      if (!presignedUrlResponse.ok) {
        if (presignedUrlResponse.status === 409 && retries < MAX_RETRIES) {
          await new Promise(resolve => setTimeout(resolve, RETRY_DELAY));
          sessionStorage.setItem(SESSION_ID_KEY, uuidv4());
          return await uploadFile(file, fileId, retries + 1);
        }

        let errorMessage = 'Failed to get pre-signed URL.';
        try {
          const errorData = await presignedUrlResponse.json();
          if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch (e) {
          // ignore if parsing fails
        }
        throw new Error(errorMessage);
      }

      const { url } = await presignedUrlResponse.json();

      const uploadResponse = await fetch(url, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type,
        },
      });

      if (!uploadResponse.ok) {
        throw new Error('Failed to upload file to S3.');
      }

      setFiles(prev => prev.map(f => f.id === fileId ? { ...f, status: 'completed' } : f));
    } catch (error) {
      console.error(`Upload failed for ${file.name}:`, error);
      setFiles(prev => prev.map(f => f.id === fileId ? { ...f, status: 'failed', error: error.message } : f));
      throw error;
    }
  };

  const handleUpload = async () => {
    if (awsRum) {
      awsRum.recordEvent('uploadButtonClick', {})
    }
    if (!name) {
      setUploadError('Please enter your name.');
      return;
    }

    if (files.length === 0) {
      setUploadError('Please select files to upload.');
      return;
    }

    setIsUploading(true);
    setUploadInitiated(true);
    setUploadError(null);
    setUploadSuccess(false);

    const filesToUpload = files.filter(f => f.status !== 'completed');
    const results = [];

    for (let i = 0; i < filesToUpload.length; i += UPLOAD_BATCH_SIZE) {
      const batch = filesToUpload.slice(i, i + UPLOAD_BATCH_SIZE);
      const batchPromises = batch.map(({ id, file }) => uploadFile(file, id));
      const batchResults = await Promise.allSettled(batchPromises);
      results.push(...batchResults);
    }

    try {
      const allSucceeded = results.every(result => result.status === 'fulfilled');

      if (allSucceeded) {
        setUploadSuccess(true);
        setUploadComplete(true);
      } else {
        const failedCount = results.filter(r => r.status === 'rejected').length;
        const succeededCount = results.length - failedCount;
        setUploadError(`Upload summary: ${succeededCount} succeeded, ${failedCount} failed.`);
      }
    } catch (error) {
      console.error('An unexpected error occurred during the upload process:', error);
      setUploadError('An unexpected error occurred.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleUploadMore = () => {
    if (awsRum) {
      awsRum.recordEvent('uploadMoreButtonClick', {})
    }
    setFiles([]);
    setUploadSuccess(false);
    setUploadInitiated(false);
    setIsUploading(false);
    setUploadComplete(false);
  };

  let content;
  if (magicWordState === 'validating') {
    content = <p className="message">Validating...</p>;
  } else if (magicWordState === 'invalid') {
    content = <p className="message">Invalid or expired link, ask the wedding party for the secret link.</p>;
  } else if (magicWordState === 'missing') {
    content = <p className="message">Ask the wedding party for the secret link.</p>;
  } else if (magicWordState === 'valid') {
    content = (
      <div className="fade-in">
        <div className="name-container">
          <input
            id="name-input"
            type="text"
            placeholder="Your Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="name-input"
            disabled={isUploading || uploadComplete}
            required
          />
        </div>
        <FileUploader onFilesSelected={handleFilesSelected} disabled={isUploading || uploadComplete} />
        <div className="file-previews">
          {files.map(fileWrapper => (
            <FilePreview key={fileWrapper.id} fileWrapper={fileWrapper} onRemove={handleRemoveFile} uploadInitiated={uploadInitiated} uploadComplete={uploadComplete} />
          ))}
        </div>
        {!uploadComplete ? (
          <button onClick={handleUpload} className="upload-btn" disabled={isUploading || files.length === 0}>
            {isUploading ? 'Uploading...' : 'Upload file(s)'}
          </button>
        ) : (
          <button onClick={handleUploadMore} className="upload-btn">
            Upload more files
          </button>
        )}
        <p className="upload-info">
          We will be collecting the photos and creating a photo gallery that we'll share with everyone after the wedding!
        </p>
      </div>
    );
  }

  return (
    <div className="App">
      <header className="App-header">
        <h1>Wedding Pix</h1>
      </header>
      <div className="uploader-container">
        <Message message={uploadError} type="error" />
        <Message message={uploadSuccess ? 'All files uploaded successfully!' : null} type="success" />
        {content}
      </div>
    </div>
  );
}

export default App;

